import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const VALUES = [
  ['01', { ar: 'الالتزام والمساءلة الشخصية', en: 'Commitment and personal accountability' }, '1'],
  ['02', { ar: 'الربحية نتيجةٌ وليست هدفاً', en: 'Profitability is an outcome, not a goal' }, '1'],
  ['03', { ar: 'التطوير حلقةٌ مستمرة', en: 'Development is a continuous cycle' }, '2'],
  ['04', { ar: 'الاحتراف أسلوب حياةٍ والتزام', en: 'Professionalism is a way of life and a commitment' }, '2'],
  ['05', { ar: 'إيجاد الحلول', en: 'Finding solutions' }, '3'],
  ['06', { ar: 'الإبداع والجودة والإنجاز', en: 'Creativity, quality and achievement' }, '1'],
  ['07', { ar: 'التدريب وتنمية الذات', en: 'Training and self-development' }, '2'],
  ['08', { ar: 'إدارة وتوظيف المعرفة', en: 'Managing and leveraging knowledge' }, '2'],
  ['09', { ar: 'تعظيم الفاعلية منهجاً', en: 'Maximizing effectiveness as a methodology' }, '3'],
  ['10', { ar: 'المسؤولية المجتمعية', en: 'Social responsibility' }, '3'],
];

const GOALS = [
  ['01', { ar: 'تكوين مجتمعٍ استثماري إنتاجي لتحقيق تنميةٍ حقيقية مستدامة بأعلى فاعلية اقتصادية وتشغيلية.', en: 'Building a productive investment community to achieve genuine, sustainable development with the highest economic and operational efficiency.' }, '1'],
  ['02', { ar: 'تحقيق أقصى معدّل كفاية من خلايا إنتاجية تتناسب مع حجم الموارد المتاحة لكل منطقة.', en: 'Achieving the highest efficiency rate through production cells matched to the volume of resources available in each region.' }, '2'],
  ['03', { ar: 'رفع الكفاية التشغيلية على أسسٍ علمية من خلال تكامل المجالات تخفيضاً للمخاطر، لضمان استدامة التشغيل مع زيادة قاعدة الاستثمار.', en: 'Raising operational efficiency on scientific foundations through the integration of fields to reduce risk, ensuring operational sustainability while expanding the investment base.' }, '1'],
  ['04', { ar: 'التوسّع خارجياً في منتجات وخدمات المجموعة ذات الميزة النسبية، تحقيقاً لأقصى عائد وتدفقاتٍ نقدية حقيقية.', en: 'Expanding outward with the Group\'s products and services that hold a comparative advantage, achieving maximum return and real cash flows.' }, '2'],
];

export default function AboutPage() {
  const { isEN, t } = useLanguage();

  useEffect(() => {
    document.title = isEN ? 'About — Elhaware Group' : 'من نحن — مجموعة الهواري';
  }, [isEN]);

  return (
    <>
      <section className="page-hero">
        <div className="page-hero__bg">
          <div className="ph shot" data-parallax="0.08" role="img" aria-label={isEN ? "The Group's fields and farms" : "حقول ومزارع المجموعة"} style={{ backgroundImage: "url('/assets/img/agri-development.webp')" }}></div>
        </div>
        <div className="page-hero__glow"></div>
        <div className="wrap">
          <nav className="breadcrumb" data-reveal="fade"><Link to="/">{isEN ? 'Home' : 'الرئيسية'}</Link><span>/</span><span style={{ color: 'rgba(255,255,255,.85)' }}>{isEN ? 'About' : 'من نحن'}</span></nav>
          <span className="eyebrow" data-reveal>{isEN ? 'About' : 'من نحن'}</span>
          <h1 data-reveal data-delay="1">{isEN ? <>An entity that <b>bears trust</b><br />since 2015</> : <>كيانٌ <b>يثمر ثقة</b><br />منذ عام 2015</>}</h1>
          <p className="page-hero__lead" data-reveal data-delay="2">{isEN ? 'A group of companies with a fully paid-up capital of nearly EGP 200 million, which has grown steadily to become a diversified, integrated economic entity built on agricultural investment across its branches.' : 'مجموعة شركات يبلغ رأس مالها نحو 200 مليون جنيه مصري مدفوعة بالكامل، نمت بخطواتٍ ثابتة لتصبح كياناً اقتصادياً متعدّد ومتكامل الأنشطة، قائماً على الاستثمار الزراعي بفروعه.'}</p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <div className="shead" data-reveal>
            <span className="eyebrow">{isEN ? 'Our Vision and Mission' : 'رؤيتنا ومهمتنا'}</span>
            <h2>{isEN ? <>We build <b>genuine, sustainable development</b> with the highest economic and operational efficiency</> : <>نبني <b>تنميةً حقيقية مستدامة</b> بأعلى فاعلية اقتصادية وتشغيلية</>}</h2>
          </div>
          <div className="vm-grid">
            <article className="vm-card vm-card--vision" data-reveal="right">
              <div className="vm-card__mark">”</div>
              <h3>{isEN ? 'Vision' : 'الرؤية'}</h3>
              <p>{isEN ? 'The Group is a leading, distinguished economic entity with diversified and integrated activities, built on agricultural investment through the trade and production of agricultural crops, livestock and poultry, agricultural manufacturing and complementary activities, in harmony with our environmental, social and economic conditions, and helping to form a productive investment community that achieves genuine, sustainable development.' : 'المجموعة كيان اقتصادي متميّز رائد، متعدّد ومتكامل الأنشطة، قائم على الاستثمار الزراعي من تجارةٍ وإنتاجٍ لحاصلات الإنتاج الزراعي والإنتاج الحيواني والداجني والتصنيع الزراعي والأنشطة المكمّلة، بما يتناغم مع ظروفنا البيئية والاجتماعية والاقتصادية، ويساعد على تكوين مجتمعٍ استثماري إنتاجي لتحقيق تنميةٍ حقيقية مستدامة.'}</p>
            </article>
            <article className="vm-card vm-card--mission" data-reveal="left">
              <div className="vm-card__mark">”</div>
              <h3>{isEN ? 'Mission' : 'المهمة'}</h3>
              <p>{isEN ? 'Achieving the highest operational efficiency rate through production cells coupled with geographic expansion, grounded in Sharia principles, while expanding the investment base with the same orientation.' : 'تحقيق أقصى معدّل كفاية للتشغيل من خلال خلايا إنتاجٍ مع التوسّع الجغرافي، قائمةٍ على أسس الشريعة، مع زيادة قاعدة الاستثمار بذات التوجّه.'}</p>
            </article>
          </div>
        </div>
      </section>

      <section className="values section">
        <div className="wrap">
          <div className="shead center" data-reveal>
            <span className="eyebrow">{isEN ? 'Our Values' : 'قيمنا'}</span>
            <h2>{isEN ? <>Ten values that govern <b style={{ color: 'var(--champagne)' }}>the way we work</b></> : <>عشر قيمٍ تحكم <b style={{ color: 'var(--champagne)' }}>طريقة عملنا</b></>}</h2>
          </div>
          <div className="values-grid">
            {VALUES.map(([n, label, d]) => (
              <div className="value" data-reveal data-delay={d} key={n}>
                <div className="value__n num-latin">{n}</div>
                <div className="value__t">{t(label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="goals section">
        <div className="wrap">
          <div className="shead" data-reveal>
            <span className="eyebrow">{isEN ? 'Our Goals' : 'أهدافنا'}</span>
            <h2>{isEN ? <><b>Clear</b> objectives by which we measure our success</> : <>غاياتٌ <b>واضحة</b> نقيس بها نجاحنا</>}</h2>
          </div>
          <div className="goals-list">
            {GOALS.map(([n, p, d]) => (
              <article className="goal" data-reveal data-delay={d} key={n}>
                <div className="goal__n num-latin">{n}</div>
                <p>{t(p)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="strategy section">
        <div className="wrap strategy__grid">
          <div data-reveal="right">
            <span className="eyebrow">{isEN ? 'Strategy and Parameters' : 'الاستراتيجية والمحدّدات'}</span>
            <h2 style={{ fontSize: 'clamp(28px,3.6vw,44px)', fontWeight: 300, margin: '18px 0 0', color: 'var(--forest-900)' }}>{isEN ? <>Vertical and horizontal integration<br />that reduces risk and maximizes value</> : <>تكاملٌ رأسي وأفقي<br />يخفّض المخاطر ويعظّم القيمة</>}</h2>
            <ul>
              <li>{isEN ? 'Developing a portfolio of projects that grow into successful, self-sustaining businesses, providing opportunities for horizontal and vertical integration.' : 'تطوير حزمةٍ من المشاريع تنمو لتصبح أعمالاً ناجحة قائمة بذاتها، توفّر فرص تكاملٍ أفقي ورأسي.'}</li>
              <li>{isEN ? 'Strategic investments and participations in diverse activities to expand presence and diversify the asset base.' : 'استثماراتٌ استراتيجية ومشاركاتٌ في نشاطاتٍ متنوّعة لتوسيع التواجد وتنويع قاعدة الأصول.'}</li>
              <li>{isEN ? 'Targeting the levels and operating regions in greatest need, serving a sector of vital importance to society.' : 'استهداف المستويات ومناطق التشغيل الأكثر احتياجاً، بما يخدم قطاعاً مهماً للمجتمع.'}</li>
              <li>{isEN ? 'Managing investments worth EGP 500 million with annual revenues exceeding one billion.' : 'إدارة استثماراتٍ بقيمة 500 مليون جنيه بإيراداتٍ سنوية تتجاوز المليار.'}</li>
            </ul>
          </div>
          <div className="strategy__media" data-reveal="left">
            <div className="ph ph-light shot" role="img" aria-label={isEN ? "The Group's facilities and projects" : "منشآت ومشروعات المجموعة"} style={{ backgroundImage: "url('/assets/img/factory-packing.webp')" }}></div>
          </div>
        </div>
      </section>

      <section className="chairman section">
        <div className="chairman__glow"></div>
        <div className="wrap chairman__grid">
          <div className="chairman__media" data-reveal="right">
            <div className="ph shot" role="img" aria-label={isEN ? 'Dr. Haitham Ekry Elhaware' : 'دكتور هيثم عكري الهواري'} style={{ backgroundImage: "url('/assets/img/img_5df9dd5088b5.webp')", backgroundPosition: 'top center' }}></div>
          </div>
          <div data-reveal="left">
            <span className="eyebrow">{isEN ? "Chairman's Message" : 'كلمة رئيس مجلس الإدارة'}</span>
            <p className="chairman__quote" style={{ marginTop: 22 }}>{isEN ? <>«We believe that <b>profitability is an outcome, not a goal</b>, and that genuine development is built on a sustainable integration of people, land and capital.»</> : <>«نؤمن أن <b>الربحية نتيجةٌ وليست هدفاً</b>، وأن التنمية الحقيقية تُبنى على تكاملٍ مستدام بين الإنسان والأرض ورأس المال.»</>}</p>
            <div className="signature">
              <span className="signature__mark">H. Elhaware</span>
              <span className="signature__who"><b>{isEN ? 'Dr. / Haitham Ekry Elhaware' : 'دكتور / هيثم عكري الهواري'}</b><span>{isEN ? 'Chairman' : 'رئيس مجلس الإدارة'}</span></span>
            </div>
            <Link className="btn btn-gold" to="/contact" style={{ marginTop: 30 }}><span>{isEN ? 'Contact the Group' : 'تواصل مع المجموعة'}</span> <span className="arr">←</span></Link>
          </div>
        </div>
      </section>
    </>
  );
}
